import React from 'react';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';
import ContentWrapper from '../../components/Layout/ContentWrapper';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Loading from '../../components/Common/Loading';
import Login from './SubscriptionLogin';
import Detail from './SubscriptionDetail';
import UpdateBilling from './SubscriptionUpdateBilling';
import Upgrade from './SubscriptionUpgrade';
import {
  manageLogin,
  manageLogout,
  manageFetchMembership,
  manageCancelMembership,
  manageReactivateMembership,
  manageUpdateBilling,
  manageDeleteAccount,
  manageUpgradeMembership,
  manageValidateCoupon,
  manageClearCoupon,
  manageClearUpgradeResult,
  manageClearError,
} from '../../actions/subscriptionActions';
import { getLocalStoreValue, JWT_TOKEN_KEY } from '../../utils/localStorage';
import browserHistory from '../../history';

class Screen extends React.Component {
  constructor(props) {
    super(props);
    this.state = { view: 'subscription' };
  }

  goToUpdateBilling = () => {
    this.setState({ view: 'updateBilling' });
    browserHistory.push('/subscription/manage');
  };

  goToUpgrade = () => {
    this.setState({ view: 'upgrade' });
    browserHistory.push('/subscription/manage');
  };

  goBackToSubscription = () => this.setState({ view: 'subscription' });

  componentDidMount() {
    const { manage, doFetchMembership } = this.props;
    const hasToken = manage.token || getLocalStoreValue(JWT_TOKEN_KEY);
    if (hasToken) {
      doFetchMembership();
    }
    this.unlistenHistory = browserHistory.listen((location, action) => {
      if (action === 'POP' && this.state.view !== 'subscription') {
        this.setState({ view: 'subscription' });
      }
    });
  }

  componentWillUnmount() {
    if (this.unlistenHistory) this.unlistenHistory();
  }

  render() {
    const {
      manage,
      doLogin,
      doLogout,
      doCancel,
      doReactivate,
      doUpdateBilling,
      doDeleteAccount,
      doUpgrade,
      doValidateCoupon,
      doClearCoupon,
      doClearUpgradeResult,
      doClearError,
    } = this.props;

    const { token, membership, cancellationReasons, membershipOptions, loginError, error, membershipNotFound, updateBillingError, upgradeError, upgradeResult, couponResponse, accountDeleted, cancelInProgress } = manage;
    const hasToken = token || getLocalStoreValue(JWT_TOKEN_KEY);
    const { view } = this.state;

    const showSubscription = hasToken && membership && !accountDeleted && view === 'subscription';
    const showUpdateBilling = hasToken && membership && !accountDeleted && view === 'updateBilling';
    const showUpgrade = hasToken && membership && !accountDeleted && view === 'upgrade';

    return (
      <div className="background-container">
        <div className="wrapper pre-auth">
          <Header
            type="pre"
            onLogout={hasToken ? doLogout : null}
          />
          <section className="section-container">
            <ContentWrapper>
              <Row>
                {!showSubscription && <Col md={3} />}
                <Col
                  md={showSubscription ? 12 : 6}
                  className={showSubscription ? 'mb-3' : 'd-flex flex-column align-items-center mb-3'}
                >
                  <div className={showSubscription ? 'manage-subscription-wrapper' : 'signup-form-wrapper'}>
                    {!hasToken && (
                      <Login
                        onLogin={doLogin}
                        loginError={loginError}
                        teamsList={manage.teamsList}
                        pendingCredentials={manage.pendingCredentials}
                      />
                    )}
                    {hasToken && !membership && !error && !membershipNotFound && (
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                        <Loading />
                      </div>
                    )}
                    {hasToken && (error || membershipNotFound) && !membership && (
                      <div className="text-center">
                        <p className="text-danger mb-3">
                          {membershipNotFound ? 'No active subscription found for this account.' : error}
                        </p>
                        <button type="button" className="btn btn-link p-0 text-muted" onClick={doLogout}>
                          Sign out and try a different account
                        </button>
                      </div>
                    )}
                    {hasToken && accountDeleted && (
                      <div className="text-center py-5">
                        <i className="fas fa-check-circle fa-3x text-success mb-3" />
                        <h3>Account Deleted</h3>
                        <p className="text-muted">Your account has been successfully deleted.</p>
                      </div>
                    )}
                    {showSubscription && (
                      <Detail
                        membership={membership}
                        cancellationReasons={cancellationReasons}
                        membershipOptions={membershipOptions}
                        error={error}
                        cancelInProgress={cancelInProgress}
                        onCancel={doCancel}
                        onReactivate={doReactivate}
                        onDeleteAccount={doDeleteAccount}
                        onClearError={doClearError}
                        onGoToUpdateBilling={this.goToUpdateBilling}
                        onGoToUpgrade={this.goToUpgrade}
                        onLogout={doLogout}
                      />
                    )}
                    {showUpdateBilling && (
                      <UpdateBilling
                        error={updateBillingError}
                        onSuccess={(paymentMethodId) => {
                          doUpdateBilling(membership.id, paymentMethodId);
                          this.goBackToSubscription();
                        }}
                        onBack={this.goBackToSubscription}
                      />
                    )}
                    {showUpgrade && (
                      <Upgrade
                        membership={membership}
                        membershipOptions={membershipOptions}
                        upgradeError={upgradeError}
                        upgradeResult={upgradeResult}
                        couponResponse={couponResponse}
                        onUpgrade={doUpgrade}
                        onValidateCoupon={doValidateCoupon}
                        onClearCoupon={doClearCoupon}
                        onClearUpgradeResult={doClearUpgradeResult}
                        onBack={this.goBackToSubscription}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </ContentWrapper>
          </section>
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  manage: state.manage,
});

const mapDispatchToProps = (dispatch) => ({
  doLogin: (username, password, teamId) => dispatch(manageLogin(username, password, teamId)),
  doLogout: () => dispatch(manageLogout()),
  doFetchMembership: () => dispatch(manageFetchMembership()),
  doCancel: (membershipId, cancellationReasonId) =>
    dispatch(manageCancelMembership(membershipId, cancellationReasonId)),
  doReactivate: (membershipId) => dispatch(manageReactivateMembership(membershipId)),
  doUpdateBilling: (membershipId, paymentMethodId) =>
    dispatch(manageUpdateBilling(membershipId, paymentMethodId)),
  doDeleteAccount: () => dispatch(manageDeleteAccount()),
  doUpgrade: (membershipId, productId, enteredCoupon) =>
    dispatch(manageUpgradeMembership(membershipId, productId, enteredCoupon)),
  doValidateCoupon: (enteredCoupon, productId) =>
    dispatch(manageValidateCoupon(enteredCoupon, productId)),
  doClearCoupon: () => dispatch(manageClearCoupon()),
  doClearUpgradeResult: () => dispatch(manageClearUpgradeResult()),
  doClearError: () => dispatch(manageClearError()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Screen);
